import { useTranslation } from 'react-i18next'
import {
  CTColor,
  CTLabel,
  CTType,
  CTValue,
  CTLine,
  CTTitle,
  CustomTooltipContainer,
  CTTotalSection,
  CTAverageSection,
  CTLeftContent,
  CTEstimatedText,
} from './TooltipStyles'
import { Box } from '@mui/material'
import React, { useMemo } from 'react'
import { useGetStatsTimeRange } from '../../store'
import { TimeRange } from 'models'
import prettyBytes from 'pretty-bytes'

interface TooltipGraphProps {
  active?: boolean;
  /* eslint-disable @typescript-eslint/no-explicit-any */ 
  payload?: any[];
  label?: any;
  dataType: 'type' | 'ssid' | 'site';
  isTraffic?: boolean;
}

const TooltipGraph: React.FC<TooltipGraphProps> = ({
  active = false,
  payload: metrics = [],
  label,
  dataType,
  isTraffic = false,
}) => {
  const { t, i18n } = useTranslation()
  const [selectedTimeRange] = useGetStatsTimeRange()

  const dateTimeFormatStyle = useMemo<Intl.DateTimeFormatOptions>(() => {
    if (
      [
        TimeRange.LastSevenDays,
        TimeRange.LastFourteenDays,
        TimeRange.LastThirtyDays,
        TimeRange.LastMonth,
      ].includes(selectedTimeRange)
    ) {
      return {
        dateStyle: 'full',
      }
    } else {
      return {
        month: 'long',
        year: 'numeric',
      }
    }
  }, [selectedTimeRange])

  const currentData = metrics[0]?.payload || {}
  const isEstimated = currentData.is_estimated || false
  const averagePerDevice = dataType === 'type' 
    ? currentData.average_per_device_type 
    : (dataType === 'ssid' ? currentData.average_per_device_ssid : currentData.average_per_device_site)

  const filteredMetrics = useMemo(() => {
    const dataMap = new Map()
    
    metrics.forEach((item) => {
      if (item.value > 0) {
        const baseKey = item.dataKey.replace('_estimated', '')
        
        if (!dataMap.has(baseKey)) {
          dataMap.set(baseKey, {
            ...item,
            dataKey: baseKey
          })
        }
      }
    })
    
    return Array.from(dataMap.values()).sort((a, b) => b.value - a.value)
  }, [metrics])

  const totalValue = useMemo(() => {
    const total = filteredMetrics
      .map((item) => item.value)
      .reduce((accumulator, itemValue) => accumulator + itemValue, 0)
    
    if (isTraffic) {
      return total.toString().replace(/,/g, ' ')
    }
    return prettyBytes(total * 1_000_000, { locale: i18n.language, minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace('B', 'o')
  }, [filteredMetrics, isTraffic, i18n.language])

  const formattedAveragePerDevice = useMemo(() => {
    if (isTraffic || !averagePerDevice) return null
    return prettyBytes(averagePerDevice * 1_000_000, { locale: i18n.language, minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace('B', 'o')
  }, [averagePerDevice, isTraffic, i18n.language])

  const displayTimestamp = label || currentData.timestamp

  if (!active || !filteredMetrics.length) {
    return null
  }

  const formattedDate = new Intl.DateTimeFormat(i18n.language, dateTimeFormatStyle).format(displayTimestamp)

  const tooltipWidth = !isTraffic ? '500px' : '420px'

  return (
    <CustomTooltipContainer sx={{ width: tooltipWidth }}>
      <CTTitle>
        {formattedDate.toUpperCase()}
        {isEstimated && (
          <CTEstimatedText component="span">
            ({t('stats.estimated')})
          </CTEstimatedText>
        )}
      </CTTitle>
      
      {filteredMetrics.map((item, index) => {
        let itemName = item.dataKey
        let networkType = null
        
        if (dataType === 'ssid') {
          const parts = item.dataKey.split('_')
          itemName = parts[0]
          networkType = parts[1]
        } else if (dataType === 'site') {
          const parts = item.dataKey.split('_')
          itemName = parts.slice(0, -1).join('_')
        }
        
        const formattedValue = isTraffic
          ? `${item.value.toString().replace(/,/g, ' ')} ${t('stats.legends.devices')}`
          : prettyBytes(item.value * 1_000_000, { locale: i18n.language, minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace('B', 'o')
        
        return (
          <CTLine key={`${item.dataKey}-${index}`}>
            <CTLeftContent sx={{ minWidth: '200px', flex: '0 0 200px' }}>
              <CTColor color={item.fill || item.stroke || item.color} />
              <div>
                <CTLabel>
                  {dataType === 'type' 
                    ? t(`stats.legends.types.${item.dataKey}`) 
                    : itemName
                  }
                </CTLabel>
                {dataType === 'ssid' && networkType && (
                  <CTType>{t(`stats.legends.types.${networkType}` as any)}</CTType>
                )}
              </div>
            </CTLeftContent>
            
            <Box sx={{ 
              flex: 1,
              height: '1px', 
              backgroundColor: 'white', 
              mx: 1
            }} />
            
            <CTValue>
              {formattedValue}
              {isEstimated && (
                <CTEstimatedText component="span">
                  ({t('stats.estimated')})
                </CTEstimatedText>
              )}
            </CTValue>
          </CTLine>
        )
      })}
      
      <Box sx={{ 
        display: 'flex',
        justifyContent: 'flex-end',
        mt: 1
      }}>
        <CTTotalSection>
          <Box>{t('stats.legends.total')}</Box>
          <Box>
            {totalValue}
            {isTraffic && ` ${t('stats.legends.devices')}`}
            {isEstimated && (
              <CTEstimatedText component="span">
                ({t('stats.estimated')})
              </CTEstimatedText>
            )}
          </Box>
        </CTTotalSection>
      </Box>
      
      {formattedAveragePerDevice && (
        <CTAverageSection>
          <Box>{t('stats.legends.average_per_device')}</Box>
          <Box>
            {formattedAveragePerDevice}
            {isEstimated && (
              <CTEstimatedText component="span">
                ({t('stats.estimated')})
              </CTEstimatedText>
            )}
          </Box>
        </CTAverageSection>
      )}
    </CustomTooltipContainer>
  )
}

export default TooltipGraph
